import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useParams, useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import _debounce from 'lodash/debounce';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Badge from "@material-ui/core/Badge";
import Menu from '@material-ui/core/Menu';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FilterIcon from "@material-ui/icons/Tune";
import CloseIcon from '@material-ui/icons/Close';
import ArrowDown from '@material-ui/icons/ArrowDropDown';
import ArrowUp from '@material-ui/icons/ArrowDropUp';
import {doubleDash, singleDashSpace, allWords, titleCase} from '../lib/urlTools';
import HelmetSEO from '../components/AgentSearch/HelmetSEO';
import AgentAgencyButtons from '../components/AgentSearch/AgentAgencyButtons';
import ResultSummary from '../components/AgentSearch/ResultSummary';
import FilterInput from '../components/AgentSearch/FilterInput';
import SortOptions from '../components/AgentSearch/SortOptions';
import SurroundOptions from '../components/AgentSearch/SurroundOptions';
import Loading from '../components/Loading/Loading';
import SuburbTypeahead from '../components/Typeahead/SuburbTypeahead';
import Pagination from '../components/Pagination/Pagination';
import AdSlot from '../components/AdSlot/AdSlot';
import PageContainer from './PageContainer';
import AgentCard from '../components/AgencyCards/AgentCard_mui';
import AgencyCard from '../components/AgencyCards/AgencyCard_mui';
import styles from './AgentSearch.scss';
import {fa_doSearch} from '../sagas/FindAgencies';
import {doAgentSearch, getRmaAgencyProfiles, getRmaAgentProfiles} from '../actions';
import {agencySearchFormatter} from '../selectors/agencySearchFormatter';
import PagedSEO from '../components/Pagination/PagedSEO';
import {breakpointMdLow} from '../lib/config';
import {RmaFooterDisclaimer} from '../components/RateMyAgent/Ratings';
import Disclaimer from '../components/Disclaimer/Disclaimer';
import {formatAddressTargeting} from '../lib/adFunctions';

const useStyles = makeStyles(theme => ({
  iconButton: {
    backgroundColor: theme.palette.primary.main,
    color: '#FFF',
    borderRadius: '4px',
    minWidth: '36px',
  },
}));

const prettyLocation = (params) => {
  let s = params.suburb;
  s = s.replace(doubleDash, '@');
  s = s.replace(singleDashSpace, ' ');
  s = s.replace(/@/g, '-');
  s = s.replace(allWords, titleCase);
  return {
    suburb: s,
    postcode: params.postcode,
    stateCode: params.stateCode.toUpperCase(),
  };
}

const sortFields = [
  {field: 'currentRecentSalesCount', label: 'No. of properties sold'},
  {field: 'currentSaleListingCount', label: 'No. of properties for sale'},
  {field: 'currentRentalListingCount', label: 'No. of properties for rent'},
  {field: 'nameASC', label: 'Name (A-Z)'},
  {field: 'nameDESC', label: 'Name (Z-A)'},
];

const defaults = {
  sort: 'currentRecentSalesCount',
  filter: '',
  page: 1,
  localAgencies: false,
  surroundingSuburbs: false,
}

function AgentSearch() {
  // on desktop any filter/sort change is run immediately, but on mobile the user has to click for the search to run
  // user interaction only changes the url
  // a change in the url will trigger a search
  const isMobile = () => (typeof window != 'undefined' && (window?.oth_device === 'phone' || window?.innerWidth < breakpointMdLow)) ?  true : false;
  const searchAgencies = useSelector(state => agencySearchFormatter(state.searchAgencies));
  const [isSmallScreen, setIsSmallScreen] = useState(isMobile());
  const [currentSearch, setCurrentSearch] = useState({sort: defaults.sort, filter: defaults.filter, page: defaults.page, localAgencies: defaults.localAgencies, surroundingSuburbs: defaults.surroundingSuburbs});
  const [sortShowing, setSortShowing] = useState(false);
  const [filterDotShowing, setFilterDotShowing] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const history = useHistory();
  const menuAnchor = useRef(null);
  const classes = useStyles();
  const resizeHandler = () => {
    setIsSmallScreen(isMobile());
  }
  const dbounceResize = _debounce(resizeHandler, 250);

  const buildSearchObjFromURL = () => {
    let newSearch = {
      localAgencies: defaults.localAgencies,
      surroundingSuburbs: defaults.surroundingSuburbs,
      page: defaults.page,
      sort: defaults.sort,
      filter: defaults.filter,
      ...params
    }
    if (location.search != '') {
      let queryParamsArr = location.search.replace('?', '').split('&');
      queryParamsArr.map((qp) => {
        qp = qp.split('=');
        newSearch[qp[0]] = qp[1];
      });
    }
    return newSearch;
  }

  useEffect(() => {
    let urlSearchObj = buildSearchObjFromURL();
    if (searchAgencies.status === 'success') { // ssr has run
      // if the search results don't match the url search obj, run the search
      let searchKey = JSON.parse(searchAgencies.agencyList.key);
      searchKey = {...urlSearchObj, ...searchKey};
      if (_isEqual(urlSearchObj, searchKey)) setCurrentSearch(urlSearchObj); // update the search obj, search doesn't need to run
      else {
        setCurrentSearch(urlSearchObj);
        runSearch(urlSearchObj); // run the search with the new search obj
      }
    }
    else setCurrentSearch(urlSearchObj); // we've mounted without an existing search, like coming from find-agent for the first time

    window.addEventListener("resize", dbounceResize);
    return () => window.removeEventListener("resize", dbounceResize);
  }, []);

  useEffect(() => {
    let urlSearchObj = buildSearchObjFromURL();
    if (searchAgencies.status === 'success') {
      let searchKey = JSON.parse(searchAgencies.agencyList.key)
      // searchKey can come from ssr and only have basic keys. for comparison the missing keys should be added as defaults
      let skKeys = Object.keys(searchKey);
      let missingKeys = Object.keys(urlSearchObj).filter((sok) => !skKeys.includes(sok));
      if (missingKeys.length > 0) missingKeys.map((key) => {searchKey[key] = defaults[key]});
      // and if the url search isn't the same as the results search then update the search
      if (!_isEqual(urlSearchObj, searchKey)) runSearch(urlSearchObj);
    }
    else runSearch(urlSearchObj); // no search results currently exist
  }, [location]);

  useEffect(() => {
    // on dektop - everytime i change, i need to change the url. on mobile the url needs to change when the user clicks search
    if (!isMobile()) newSearchURL();
    else redDotCheck();
  }, [currentSearch]);

  useEffect(() => {
    if (searchAgencies.status === 'success') {
      const ids = [];
      if (params.agentDisplay === 'agencies') {
        searchAgencies.agencyList.content.map(({agencyId}) => ids.push(agencyId));
        !!ids.length && dispatch(getRmaAgencyProfiles(ids));
      } else {
        searchAgencies.agencyList.content.map(({uuid}) => ids.push(uuid));
        !!ids.length && dispatch(getRmaAgentProfiles(ids));
      }
    }
  }, [searchAgencies]);

  const newSearchURL = (reset=false) => {
    if (currentSearch?.agentDisplay) { // don't do it when currentSearch inits
      let qsArr = [];
      let newURL = `/real-estate-${currentSearch.agentDisplay}/${currentSearch.stateCode}/${currentSearch.suburb}-${currentSearch.postcode}`;
      if (!reset) { // reset wants a clean url with no filters
        ['sort', 'filter', 'page', 'localAgencies', 'surroundingSuburbs'].map((qp) => {
          // only put them in if they're different or not default
          if (currentSearch.hasOwnProperty(qp) && currentSearch[qp] !== defaults[qp]) qsArr.push(`${qp}=${currentSearch[qp]}`);
        })
        if (qsArr.length > 0) newURL = `${newURL}?${qsArr.join('&')}`;
      }
      if (newURL !== `${location.pathname}${location.search}${location.hash}`) history.push(newURL);
    }
  }

  const runSearch = (searchObj) => {
    // I run the actual search query
    // when on mount for ssr
    // or if the existing search is not this search
    let search = currentSearch;
    if (searchObj) {
      search = searchObj;
      setCurrentSearch(search);
    }
    dispatch(doAgentSearch(search));
  }

  const updateCurrentSearch = (newValsArr) => {
    if (newValsArr?.length) {
      let cSearch = _cloneDeep(currentSearch);
      newValsArr.map((nv) => {
        let key = Object.keys(nv)[0];
        cSearch[key] = nv[key];
      });
      setCurrentSearch(cSearch);
    }
  }

  const redDotCheck = () => {
    let isFiltered = (currentSearch.sort != defaults.sort || currentSearch.filter != defaults.filter || currentSearch.localAgencies !== defaults.localAgencies || currentSearch.surroundingSuburbs !== defaults.surroundingSuburbs);
    if (isFiltered && !filterDotShowing) setFilterDotShowing(true);
    if (!isFiltered && filterDotShowing) setFilterDotShowing(false);
  }

  const showSort = () => {
    setSortShowing(!sortShowing);
  }

  const setSortMobile = (cSort) => {if (cSort !== currentSearch.sort) updateCurrentSearch([{sort: cSort}, {page: 1}]);}
  const setSortDesktop = (cSort) => {
    updateCurrentSearch([{sort: cSort}, {page: 1}]);
    if (sortShowing) setSortShowing(false);
  }
  const setFilterMobile = (cFilterText) => {if (cFilterText !== currentSearch.filter) updateCurrentSearch([{filter: cFilterText}, {page: 1}]);}
  const dbounceFilterMobile = _debounce(setFilterMobile, 350);
  const setFilterDesktop = (cFilterText) => {
    if (cFilterText !== currentSearch.filter) updateCurrentSearch([{filter: cFilterText}, {page: 1}]);
    if (sortShowing) setSortShowing(false);
  }
  const dbounceFilterDesktop = _debounce(setFilterDesktop, 350);

  const mobileSearch = () => { // clicks search on mobile
    redDotCheck();
    newSearchURL();
    if (sortShowing) setSortShowing(false);
  }
  const resetFilters = () => { // click reset on mobile
    updateCurrentSearch([{sort: defaults.sort}, {filter: defaults.filter}]);
    if (sortShowing) setSortShowing(false);
    if (filterDotShowing) setFilterDotShowing(false);
    newSearchURL(true);
  }
  const resetSort = () => {
    // mobile only, when the dialog is cancelled set the currentSearch back to the url state and close
    setCurrentSearch(buildSearchObjFromURL());
    if (sortShowing) setSortShowing(false);
  }

  const renderRmaDataDisclaimer = () => {
    if (searchAgencies.agencyList.content.length > 0) {
      return <RmaFooterDisclaimer />;
    }
  };

  return (
    <PageContainer class="mui-bg">
      <HelmetSEO total={searchAgencies?.agencyList?.totalElements} display={params.agentDisplay} location={prettyLocation(params)} />
      <img alt="" src="https://trixels.ratemyagent.com.au/trixel/init" />
      <div className={`shadow-sm ${styles.agentSearchHeader}`}>
        <div className="d-flex flex-column justify-content-center w-100">
          <div className="d-flex flex-column flex-md-row-reverse justify-content-center w-100">
            <div className="d-flex flex-row align-items-center mb-2 mb-md-0 ml-0 ml-md-4">
              <div className={`flex-grow-1 ${styles.sta}`}><SuburbTypeahead size="small" /></div>
              <div className={`d-flex d-md-none pl-2 ${styles.iconButton}`}>
                <IconButton classes={{root: classes.iconButton}} onClick={showSort} size="small" aria-label="close icon">
                  {filterDotShowing ?
                    <Badge className={styles.badge} badgeContent=" " overlap="circular" variant="dot">
                      <FilterIcon />
                    </Badge>
                  : <FilterIcon />}
                </IconButton>
              </div>
              <div className="d-none d-md-block ml-4"><FilterInput active={params.agentDisplay} currentFilter={currentSearch.filter} returnFunc={dbounceFilterDesktop} /></div>
            </div>
            <div className="d-flex"><AgentAgencyButtons active={params.agentDisplay} url={`${params.stateCode}/${params.suburb}-${params.postcode}`} /></div>
          </div>
          {params.agentDisplay === 'agencies' && !isSmallScreen && <div className="d-none d-md-block"><SurroundOptions localAgencies={Boolean(currentSearch.localAgencies)} surroundingSuburbs={Boolean(currentSearch.surroundingSuburbs)} returnFunc={updateCurrentSearch} /></div>}
        </div>
      </div>

      <div className="container d-flex flex-column align-items-center">
        <div className={`mb-5 ${styles.resultsContainer}`}>
          {searchAgencies.status == 'fetching' && <Loading/>}
          {searchAgencies.status == 'success' &&
            <React.Fragment>
              <PagedSEO lastPage={searchAgencies.agencyList.totalPages} pageParam="page"/>
              {searchAgencies.agencyList.content.length > 0 ?
                <div className="pt-4">
                  <AdSlot adId="21817596584" pageTargeting={formatAddressTargeting(prettyLocation(params))} />

                  <div className="d-flex justify-content-between mt-3 mb-3">
                    <ResultSummary active={params.agentDisplay} count={searchAgencies?.agencyList?.totalElements} location={prettyLocation(params)} />
                    <div className="d-none d-md-block">
                      <div>Sort By</div>
                      <div><span ref={menuAnchor} className={styles.sortLink} onClick={showSort}>{sortFields.find((item) => {return item.field === currentSearch.sort}).label}{sortShowing ? <ArrowUp /> : <ArrowDown />}</span></div>
                    </div>
                  </div>

                  {searchAgencies.agencyList.content.map((item, index) => {
                      item.referrer = location;
                      return params.agentDisplay == 'agencies' ? <AgencyCard agency={item} key={index} /> : <AgentCard agent={item} key={index} />
                  })}
                  <AdSlot adId="21817597313" pageTargeting={formatAddressTargeting(prettyLocation(params))} />
                </div>
              :
                <div className={styles.noResults}>
                  <Typography variant="h5" className="text-center">We didn't find any {params.agentDisplay == 'agencies' ? 'Agencies' : 'Agents'} that match your search criteria</Typography>
                  <Typography variant="body2">Try changing your filters to get more results</Typography>
                  <AdSlot adId="21817596584" pageTargeting={formatAddressTargeting(prettyLocation(params))} />
                </div>
              }
              {!isSmallScreen &&
                <Menu className={styles.sortMenu} anchorEl={menuAnchor?.current} open={sortShowing} onClose={showSort}>
                  <div className="p-2"><SortOptions sort={currentSearch.sort} fields={sortFields} returnFunc={setSortDesktop} /></div>
                </Menu>
              }
              {isSmallScreen &&
                <Dialog className={styles.sortDialog} fullScreen onClose={showSort} open={sortShowing}>
                  <IconButton className={styles.closeDialog} aria-label="close" onClick={resetSort}><CloseIcon /></IconButton>
                  <DialogContent dividers>
                    <Typography variant="h4" className="mt-5 mb-3">Filter your search</Typography>
                    <FilterInput active={params.agentDisplay} currentFilter={currentSearch.filter} returnFunc={dbounceFilterMobile} />
                    {params.agentDisplay === 'agencies' && <SurroundOptions localAgencies={Boolean(currentSearch.localAgencies)} surroundingSuburbs={Boolean(currentSearch.surroundingSuburbs)} returnFunc={updateCurrentSearch} />}
                    <Typography variant="h4" className="mt-5 mb-3">Sort by</Typography>
                    <SortOptions sort={currentSearch.sort} fields={sortFields} returnFunc={setSortMobile} />
                  </DialogContent>
                  <DialogActions className="flex-column justify-content-center p-4">
                    <Button fullWidth onClick={mobileSearch} color="primary" className="mb-2">Apply</Button>
                    <div className={styles.sortLink} onClick={resetFilters}>Reset filters</div>
                  </DialogActions>
                </Dialog>
              }
              <Pagination totalPages={searchAgencies?.agencyList?.totalPages} pageParam="page" />
              {renderRmaDataDisclaimer()}
              <Disclaimer stateCodeList={Array.of(params.stateCode)}/>
            </React.Fragment>
          }
        </div>
      </div>
    </PageContainer>
  );
}

function preloadSaga() {
  return [fa_doSearch];
}

AgentSearch.preloadSaga = preloadSaga;

export default AgentSearch;

